/**
 * Price Book: supplier costs item by item, the current price of each, and a
 * Stale badge when no price is valid today. Filters on top; Excel export.
 * Imports arrive through the integration API (Anna), so there is no form here.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Download, Search } from 'lucide-react';
import { ApiError, api, apiDownload } from '../../lib/api';
import { cost, staleLabel, validity, vatLabel, type Currency, type VatBasis } from '../../lib/pricebook';
import { HelpButton } from '../../components/HelpButton';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  filterClass,
} from '../../components/ui';

export interface PriceRow {
  id: string;
  supplier: { id: string; code: string; name: string };
  partNo: string | null;
  model: string | null;
  brand: string | null;
  description: string | null;
  category: { id: string | null; code: string | null; label: string } | null;
  unit: string | null;
  current: {
    versionNo: number;
    cost: string;
    vatBasis: VatBasis;
    currency: Currency;
    validFrom: string | null;
    validUntil: string | null;
    priceListRef: string | null;
  } | null;
  stale: boolean;
  staleReason: 'EXPIRED' | 'NOT_YET_VALID' | null;
  versionCount: number;
}

interface Facets {
  suppliers: { id: string; code: string; name: string }[];
  brands: string[];
  categories: { value: string; label: string }[];
}

const PAGE_SIZE = 25;

export function PriceBookPage(): JSX.Element {
  const [filters, setFilters] = useState({ q: '', supplierId: '', brand: '', category: '', stale: 'include' });
  const [page, setPage] = useState(1);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const facets = useQuery({ queryKey: ['pricebook-facets'], queryFn: () => api<Facets>('/pricebook/facets') });

  const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE), stale: filters.stale });
  if (filters.q.trim()) params.set('q', filters.q.trim());
  if (filters.supplierId) params.set('supplierId', filters.supplierId);
  if (filters.brand) params.set('brand', filters.brand);
  if (filters.category) params.set('category', filters.category);

  const items = useQuery({
    queryKey: ['pricebook', params.toString()],
    queryFn: () => api<{ data: PriceRow[]; total: number; pageSize: number; today: string }>(`/pricebook/items?${params.toString()}`),
  });

  const set = (key: keyof typeof filters) => (value: string) => {
    setPage(1);
    setFilters({ ...filters, [key]: value });
  };

  const exportExcel = async (): Promise<void> => {
    setExportError(null);
    setExporting(true);
    try {
      const query = new URLSearchParams(params);
      query.delete('page');
      query.delete('pageSize');
      await apiDownload(
        `/pricebook/export?${query.toString()}`,
        `ITCORE-SF_PriceBook_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (caught) {
      setExportError(caught instanceof ApiError ? caught.message : 'Could not export the price book.');
    } finally {
      setExporting(false);
    }
  };

  const total = items.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = Boolean(filters.q || filters.supplierId || filters.brand || filters.category || filters.stale !== 'include');

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy dark:text-white">Price Book</h1>
          <p className="text-sm text-slate-500">
            Supplier costs with their history. Internal only: never for a customer document.
          </p>
        </div>
        <div className="flex gap-2">
          <HelpButton topic="pricebook" />
          <Button variant="secondary" busy={exporting} onClick={() => void exportExcel()}>
            <Download aria-hidden="true" className="h-4 w-4" /> Export
          </Button>
        </div>
      </header>

      {exportError && <Alert tone="critical">{exportError}</Alert>}

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-40 flex-1">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />
            <input
              className={`${filterClass} w-full pl-8`}
              placeholder="Search part no., model, brand or description"
              aria-label="Search the price book"
              value={filters.q}
              onChange={(event) => set('q')(event.target.value)}
            />
          </div>
          <select
            className={`${filterClass} flex-1 sm:w-44 sm:flex-none`}
            aria-label="Supplier"
            value={filters.supplierId}
            onChange={(event) => set('supplierId')(event.target.value)}
          >
            <option value="">All suppliers</option>
            {facets.data?.suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            className={`${filterClass} flex-1 sm:w-36 sm:flex-none`}
            aria-label="Brand"
            value={filters.brand}
            onChange={(event) => set('brand')(event.target.value)}
          >
            <option value="">All brands</option>
            {facets.data?.brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          <select
            className={`${filterClass} flex-1 sm:w-40 sm:flex-none`}
            aria-label="Category"
            value={filters.category}
            onChange={(event) => set('category')(event.target.value)}
          >
            <option value="">All categories</option>
            {facets.data?.categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            className={`${filterClass} flex-1 sm:w-36 sm:flex-none`}
            aria-label="Current or stale"
            value={filters.stale}
            onChange={(event) => set('stale')(event.target.value)}
          >
            <option value="include">Current and stale</option>
            <option value="exclude">Current only</option>
            <option value="only">Stale only</option>
          </select>
        </div>

        {items.isLoading && <Skeleton className="h-64 w-full" />}
        {items.isError && <Alert tone="critical">Could not load the price book.</Alert>}

        {items.data?.data.length === 0 && (
          <EmptyState
            title={filtered ? 'Nothing matches these filters' : 'The price book is empty'}
            description={
              filtered
                ? 'Clear a filter or search for something else.'
                : 'Supplier price lists arrive through the integration API. See Help for how.'
            }
          />
        )}

        {(items.data?.data.length ?? 0) > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr>
                  <Th>Item</Th>
                  <Th>Supplier</Th>
                  <Th>Category</Th>
                  <Th align="right">Cost</Th>
                  <Th>Valid</Th>
                  <Th>Price list</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.data?.data.map((row) => {
                  const badge = staleLabel(row.stale, row.staleReason);
                  return (
                    <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <Td>
                        <Link
                          to={`/suppliers/price-book/${row.id}`}
                          className="font-medium text-navy hover:underline dark:text-white"
                        >
                          {row.model ?? row.partNo}
                        </Link>
                        <p className="text-xs text-slate-500">
                          {[row.brand, row.partNo && row.partNo !== row.model ? row.partNo : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </Td>
                      <Td>
                        {row.supplier.name}
                        <p className="font-mono text-xs text-slate-500">{row.supplier.code}</p>
                      </Td>
                      <Td>{row.category?.label ?? '—'}</Td>
                      <Td align="right" className="tabular whitespace-nowrap">
                        {row.current ? cost(row.current.cost, row.current.currency) : '—'}
                        <span className="block text-xs text-slate-500">
                          {vatLabel(row.current?.vatBasis)}
                          {row.unit ? ` / ${row.unit}` : ''}
                        </span>
                      </Td>
                      <Td>
                        <span className="whitespace-nowrap text-xs">
                          {row.current ? validity(row.current.validFrom, row.current.validUntil) : '—'}
                        </span>
                        {badge && (
                          <span className="mt-1 block">
                            <StatusChip tone={row.staleReason === 'NOT_YET_VALID' ? 'info' : 'warning'}>{badge}</StatusChip>
                          </span>
                        )}
                      </Td>
                      <Td>
                        <span className="text-xs">{row.current?.priceListRef ?? '—'}</span>
                        {row.versionCount > 1 && (
                          <span className="block text-xs text-slate-500">{row.versionCount} versions</span>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </TableWrap>
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>
                {total} item{total === 1 ? '' : 's'}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <span>
                  {page} / {lastPage}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= lastPage}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
